import { Star, ArrowRight } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import type { DashboardReview } from "./types";
import { clampScore, initialsOf } from "./utils";

export interface DashboardReviewListProps {
  reviews: DashboardReview[];
  title: string;
  viewAllLabel: string;
  emptyLabel: string;
  onViewAll?: () => void;
  className?: string;
}

function StarRow({ score }: { score: number }) {
  const s = clampScore(score);
  return (
    <div className="flex items-center gap-0.5" aria-label={`${s} / 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={cn("h-3.5 w-3.5", i < s ? "text-biz-accent" : "text-biz-border-strong")}
          weight={i < s ? "fill" : "regular"}
          aria-hidden
        />
      ))}
    </div>
  );
}

export function DashboardReviewList({
  reviews,
  title,
  viewAllLabel,
  emptyLabel,
  onViewAll,
  className,
}: DashboardReviewListProps) {
  return (
    <section
      className={cn(
        "flex flex-col rounded-biz-card border border-biz-border bg-biz-surface-elevated biz-shadow-sm",
        className,
      )}
      aria-labelledby="dash-reviews-title"
    >
      <header className="flex items-center justify-between border-b border-biz-border px-4 py-3">
        <h3 id="dash-reviews-title" className="text-sm font-semibold text-biz-foreground">
          {title}
        </h3>
        {onViewAll && reviews.length > 0 && (
          <button
            type="button"
            onClick={onViewAll}
            className="inline-flex items-center gap-1 text-xs font-semibold text-biz-primary-subtle-fg hover:underline biz-focus-ring rounded"
          >
            {viewAllLabel}
            <ArrowRight className="h-3 w-3" weight="bold" aria-hidden />
          </button>
        )}
      </header>
      {reviews.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-biz-foreground-muted">{emptyLabel}</div>
      ) : (
        <ul className="divide-y divide-biz-border" role="list">
          {reviews.map((r) => (
            <li key={r.id} className="flex items-start gap-3 px-4 py-3">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-biz-primary-subtle text-xs font-semibold text-biz-primary-subtle-fg">
                {initialsOf(r.customerName)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-semibold text-biz-foreground">
                    {r.customerName}
                  </span>
                  <span className="text-[11px] text-biz-foreground-muted">{r.dateDisplay}</span>
                </div>
                <div className="mt-0.5">
                  <StarRow score={r.score} />
                </div>
                <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-biz-foreground-muted">
                  {r.comment}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
